/**
 * useFormMemory - Per-context form memory for Add to Collection modal
 *
 * Stores form values per memory context key in localStorage.
 * Context keys: `set:{setCode}`, `folder:{folderId}`, `_default`.
 *
 * Replaces the older useSetFormMemory hook with generalized context keys
 * and support for all configurable fields.
 */
import { useLocalStorage } from '@uidotdev/usehooks';
import { useCallback } from 'react';

import type { Condition, Finish, Language } from '@/types/card';

import type { RememberedFieldKey } from './useRememberedFields';

// ============================================================================
// Types
// ============================================================================

export interface FormMemoryValues {
  folderId?: string;
  condition?: Condition;
  finish?: Finish;
  language?: Language;
  purchasePrice?: number;
  purchaseCurrency?: string;
  purchaseDate?: string;
  notes?: string;
  isAltered?: boolean;
  isSigned?: boolean;
  isPlaytest?: boolean;
}

type FormMemoryMap = Record<string, FormMemoryValues>;

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'mtgcraft:set-form-memory';

// ============================================================================
// Hook
// ============================================================================

interface UseFormMemoryResult {
  getMemory: (contextKey: string) => FormMemoryValues;
  updateMemory: (contextKey: string, values: Partial<FormMemoryValues>) => void;
  clearFieldFromMemory: (contextKey: string, field: RememberedFieldKey) => void;
}

export default function useFormMemory(): UseFormMemoryResult {
  const [memoryMap, setMemoryMap] = useLocalStorage<FormMemoryMap>(STORAGE_KEY, {});

  const getMemory = useCallback(
    (contextKey: string): FormMemoryValues => {
      return memoryMap[contextKey] ?? {};
    },
    [memoryMap],
  );

  const updateMemory = useCallback(
    (contextKey: string, values: Partial<FormMemoryValues>) => {
      setMemoryMap((prev) => {
        const existing = prev[contextKey] ?? {};
        const merged: FormMemoryValues = { ...existing };

        for (const [k, v] of Object.entries(values)) {
          if (v !== undefined && v !== null && v !== '') {
            (merged as Record<string, unknown>)[k] = v;
          } else {
            delete (merged as Record<string, unknown>)[k];
          }
        }

        // Remove entry entirely if empty
        if (Object.keys(merged).length === 0) {
          const { [contextKey]: _, ...rest } = prev;
          return rest;
        }

        return { ...prev, [contextKey]: merged };
      });
    },
    [setMemoryMap],
  );

  const clearFieldFromMemory = useCallback(
    (contextKey: string, field: RememberedFieldKey) => {
      setMemoryMap((prev) => {
        const existing = prev[contextKey];
        if (!existing) return prev;

        const updated = { ...existing };
        delete (updated as Record<string, unknown>)[field];

        if (Object.keys(updated).length === 0) {
          const { [contextKey]: _, ...rest } = prev;
          return rest;
        }

        return { ...prev, [contextKey]: updated };
      });
    },
    [setMemoryMap],
  );

  return { getMemory, updateMemory, clearFieldFromMemory };
}
